<script setup lang="ts">
import { ClockIcon, XIcon } from '@heroicons/vue/solid';

interface TemporalConflict {
    questionPeriod: string;
    filterPeriod: string;
    details?: string;
    suggestedDateFrom?: string;
    suggestedDateTo?: string;
}

interface Props {
    conflict: TemporalConflict;
    dismissible?: boolean;
}

withDefaults(defineProps<Props>(), {
    dismissible: true,
});

const emit = defineEmits<{
    (e: 'auto-correct'): void;
    (e: 'manual-edit'): void;
    (e: 'proceed'): void;
    (e: 'rephrase'): void;
    (e: 'dismiss'): void;
}>();

const onAutoCorrect = () => emit('auto-correct');
const onManualEdit = () => emit('manual-edit');
const onProceed = () => emit('proceed');
const onRephrase = () => emit('rephrase');
const onDismiss = () => emit('dismiss');

const RELATIVE_PERIOD_LABELS: Record<string, string> = {
    today: 'hoje',
    yesterday: 'ontem',
    day_before_yesterday: 'anteontem',
};

function formatDate(dateString: string): string {
    const parsed = new Date(dateString);
    if (Number.isNaN(parsed.getTime())) {
        return dateString;
    }

    return parsed.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        timeZone: 'UTC',
    });
}

function formatDateTimeRange(value: string): string {
    const parts = value.split(' - ').map((item) => item.trim());
    if (parts.length === 2) {
        return `${formatDate(parts[0])} a ${formatDate(parts[1])}`;
    }

    const isoRange = value.split(' to ').map((item) => item.trim());
    if (isoRange.length === 2) {
        return `${formatDate(isoRange[0])} a ${formatDate(isoRange[1])}`;
    }

    return formatDate(value);
}

function formatQuestionPeriod(value: string): string {
    const normalized = String(value || '')
        .trim()
        .toLowerCase();
    if (!normalized) {
        return '-';
    }

    if (RELATIVE_PERIOD_LABELS[normalized]) {
        return RELATIVE_PERIOD_LABELS[normalized];
    }

    return formatDateTimeRange(value);
}

function formatFilterPeriod(value: string): string {
    if (!value) {
        return '-';
    }

    return formatDateTimeRange(value);
}
</script>

<template>
    <div
        class="rounded-2xl border border-l-4 border-[#E5E7EB] border-l-[#F59E0B] bg-[#F8FAFC] px-5 py-4 shadow-[0px_1px_3px_rgba(0,0,0,0.05),0px_1px_2px_rgba(0,0,0,0.03)]"
    >
        <div class="flex items-start gap-4">
            <!-- Circular Icon Container -->
            <div
                class="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full border border-[#E5E7EB] bg-white text-[#D97706] shadow-sm"
            >
                <ClockIcon class="h-5 w-5" />
            </div>

            <!-- Content Column -->
            <div class="min-w-0 flex-1">
                <!-- Header Title & Pill -->
                <div class="mb-1.5 flex flex-wrap items-center gap-2">
                    <span class="text-[10px] font-bold uppercase tracking-wider text-[#737373]">
                        Período em conflito
                    </span>
                    <span
                        class="rounded bg-[#FEF3C7] px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-[#D97706]"
                    >
                        ajuste necessário
                    </span>
                </div>

                <!-- Bold Main Message -->
                <p class="text-sm font-semibold text-[#242B59]">
                    A pergunta pede um período diferente do filtro ativo.
                </p>

                <!-- Detailed Subtext -->
                <p class="mt-1 text-xs leading-relaxed text-[#626468]">
                    Revise o intervalo antes de seguir para evitar resposta inconsistente.
                </p>

                <!-- Metadata Chips (Pergunta vs Filtro) -->
                <div class="mt-3.5 flex flex-wrap items-center gap-2">
                    <div
                        class="flex items-center gap-1.5 rounded-full border border-[#E5E7EB] bg-white px-3 py-1 text-xs shadow-sm"
                    >
                        <span class="font-bold text-[#737373]">Pergunta:</span>
                        <span class="font-semibold text-[#242B59]">
                            {{ formatQuestionPeriod(conflict.questionPeriod) }}
                        </span>
                    </div>
                    <div
                        class="flex items-center gap-1.5 rounded-full border border-[#E5E7EB] bg-white px-3 py-1 text-xs shadow-sm"
                    >
                        <span class="font-bold text-[#737373]">Filtro:</span>
                        <span class="font-semibold text-[#242B59]">
                            {{ formatFilterPeriod(conflict.filterPeriod) }}
                        </span>
                    </div>
                </div>

                <!-- Optional Details -->
                <p v-if="conflict.details" class="mt-2.5 text-xs italic text-[#737373]">
                    {{ conflict.details }}
                </p>

                <!-- Actions Row -->
                <div class="mt-4 flex flex-wrap items-center gap-2">
                    <!-- Corrigir automaticamente (Primary Teal Button) -->
                    <button
                        type="button"
                        class="inline-flex items-center gap-1.5 rounded-xl bg-[#48DDC3] px-3.5 py-2 text-xs font-bold text-[#242B59] shadow-sm transition-all hover:bg-[#3bc4ab] focus:outline-none focus:ring-2 focus:ring-[#48DDC3]/50"
                        @click="onAutoCorrect"
                    >
                        <svg
                            class="h-3.5 w-3.5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            stroke-width="2.5"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                d="M13 10V3L4 14h7v7l9-11h-7z"
                            />
                        </svg>
                        Corrigir automaticamente
                    </button>

                    <!-- Editar período (Secondary outline) -->
                    <button
                        type="button"
                        class="inline-flex items-center gap-1.5 rounded-xl border border-[#E5E7EB] bg-white px-3.5 py-2 text-xs font-semibold text-[#626468] shadow-sm transition-all hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-200"
                        @click="onManualEdit"
                    >
                        <svg
                            class="h-3.5 w-3.5 text-gray-400"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            stroke-width="2"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                            />
                        </svg>
                        Editar período
                    </button>

                    <!-- Manter filtros (Secondary outline) -->
                    <button
                        type="button"
                        class="inline-flex items-center gap-1.5 rounded-xl border border-[#E5E7EB] bg-white px-3.5 py-2 text-xs font-semibold text-[#626468] shadow-sm transition-all hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-200"
                        @click="onProceed"
                    >
                        <svg
                            class="h-3.5 w-3.5 text-gray-400"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            stroke-width="2"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                        </svg>
                        Manter filtros
                    </button>

                    <!-- Reformular pergunta (Secondary outline) -->
                    <button
                        type="button"
                        class="inline-flex items-center gap-1.5 rounded-xl border border-[#E5E7EB] bg-white px-3.5 py-2 text-xs font-semibold text-[#626468] shadow-sm transition-all hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-200"
                        @click="onRephrase"
                    >
                        <svg
                            class="h-3.5 w-3.5 text-gray-400"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            stroke-width="2"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                            />
                        </svg>
                        Reformular pergunta
                    </button>
                </div>
            </div>

            <!-- Dismiss Button -->
            <button
                v-if="dismissible"
                class="rounded-lg p-1 text-[#A3A3A3] transition-colors hover:bg-black/5"
                @click="onDismiss"
            >
                <XIcon class="h-5 w-5" />
            </button>
        </div>
    </div>
</template>
